import { useRef, useState } from "react";
import { Upload, FileAudio, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";

const formatSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

export default function AudioUpload({ onChange, disabled = false }) {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    onChange?.(f);
  };

  const clear = () => {
    setFile(null);
    onChange?.(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div
      className="bg-white border border-sage rounded-2xl shadow-ambient p-6 sm:p-8"
      data-testid="audio-upload"
    >
      <div className="mb-5">
        <p className="accent-label mb-1">Or upload audio</p>
        <h3 className="font-display text-lg text-ink">Already have an audio file?</h3>
      </div>

      {!file ? (
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (disabled) return;
            const f = e.dataTransfer.files?.[0];
            if (f) handleFile(f);
          }}
          className={[
            "flex flex-col items-center justify-center gap-3 text-center",
            "rounded-xl border-2 border-dashed cursor-pointer",
            "px-6 py-8 transition-colors duration-200",
            dragOver
              ? "border-[#D47A6A] bg-[#F4F1EA]/80"
              : "border-sage bg-sand/60 hover:bg-sand",
            disabled && "opacity-60 cursor-not-allowed",
          ].join(" ")}
          data-testid="audio-upload-dropzone"
        >
          <span className="h-10 w-10 rounded-full bg-white grid place-items-center border border-sage text-terracotta">
            <Upload className="w-5 h-5" strokeWidth={1.6} />
          </span>
          <p className="text-sm text-ink">
            <span className="font-medium">Click to browse</span> or drop an audio file
          </p>
          <p className="text-xs text-ink-muted">mp3, wav, m4a, webm · up to 25 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            data-testid="audio-upload-input"
            disabled={disabled}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      ) : (
        <div
          className="flex items-center justify-between gap-4 rounded-xl bg-sand px-4 py-3"
          data-testid="audio-upload-preview"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-9 w-9 rounded-lg bg-white grid place-items-center border border-sage text-terracotta">
              <FileAudio className="w-4 h-4" strokeWidth={1.6} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink truncate">{file.name}</p>
              <p className="text-xs text-ink-muted">{formatSize(file.size)}</p>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={clear}
            data-testid="remove-audio-button"
            className="rounded-xl text-[#A85346] hover:bg-[#D47A6A]/10 hover:text-[#A85346]"
          >
            <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.6} />
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}
